import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Header from "../components/Header";
import { getCookies } from "../controllers/cookies.controller";
import globals from "../globals";

import fxRightImg from "../img/fx-right.svg";
import fxLeftImg from "../img/fx-left.svg";

import "../styles/billings.css";

interface IPix {
  valor: string;
  qrcode: string;
  url_img: string;
  vencimento: string;
}

interface IConta {
  id: number;
  contrato: string;
  valor: string;
  vencimento: string;
  status: string;
  descricao: string;
  pix?: IPix;
}

const Billings = () => {
  const navigate = useNavigate();
  const [contas, setContas] = useState<IConta[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPix, setSelectedPix] = useState<IPix | null>(null);

  useEffect(() => {
    const token = getCookies("token");
    // Token já validado pelo ProtectedRoute

    const fetchContas = async () => {
      if (!token) return;

      try {
        const response = await fetch(`${globals.apiBaseUrl}/api/contratoonline/contas`, {
          method: "GET",
          headers: {
            'Authorization': token
          }
        });

        const result = await response.json();

        if (result.status === 'success') {
          setContas(result.contas);
        } else {
          console.error("Erro ao carregar contas:", result);
        }
      } catch (error) {
        console.error("Erro ao buscar contas:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContas();
  }, []);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'R':
        return { text: 'Paga', color: '#4CAF50' };
      case 'C':
        return { text: 'Cancelada', color: '#F44336' };
      case 'P':
        return { text: 'Parcial', color: '#FF9800' };
      case 'A':
      default:
        return { text: 'Aberta', color: '#FFC107' };
    }
  };

  if (loading) {
    return (
      <React.Fragment>
        <Header />
        <div className="billings-container">
          <b className="header">Carregando...</b>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="billings-container">
        <b className="header">Últimas faturas</b>
        <span className="sub-header">Histórico de pagamento</span>

        <div className="billings-wrapper">
          {contas.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
              Você não possui contas a pagar no momento.
            </div>
          ) : (
            <table className="billings-table">
              <thead>
                <tr>
                  <td>Data</td>
                  <td>Contrato</td>
                  <td>Descrição</td>
                  <td>Total</td>
                  <td>Status</td>
                  <td>Ações</td>
                </tr>
              </thead>
              <tbody>
                {contas.map((conta) => {
                  const statusInfo = getStatusText(conta.status);
                  return (
                    <tr key={conta.id}>
                      <td>{conta.vencimento}</td>
                      <td>{conta.contrato}</td>
                      <td>{conta.descricao}</td>
                      <td className="price">
                        <span>R$</span>
                        <span className="dest">
                          {parseFloat(conta.valor).toFixed(2).split(".")[0]}
                        </span>
                        <span>
                          ,{parseFloat(conta.valor).toFixed(2).split(".")[1]}
                        </span>
                      </td>
                      <td>
                        <span style={{ color: statusInfo.color, fontWeight: "bold" }}>
                          {statusInfo.text}
                        </span>
                      </td>
                      <td>
                        {conta.pix && conta.status === 'A' && (
                          <button
                            onClick={() => setSelectedPix(conta.pix!)}
                            style={{
                              backgroundColor: "#6EE7B7",
                              color: "#000",
                              border: "none",
                              padding: "8px 16px",
                              borderRadius: "5px",
                              cursor: "pointer",
                              fontWeight: "bold"
                            }}
                          >
                            Ver PIX
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal de PIX */}
        {selectedPix && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#1a1a1a',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center'
            }}>
              <h2 style={{ color: '#fff', marginBottom: '20px' }}>Pagamento via PIX</h2>

              {selectedPix.url_img && (
                <div style={{ marginBottom: '20px' }}>
                  <img
                    src={selectedPix.url_img}
                    alt="QR Code PIX"
                    style={{ maxWidth: '100%', borderRadius: '8px' }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                <p style={{ color: '#999', margin: '10px 0' }}>
                  <strong>Valor:</strong> R$ {parseFloat(selectedPix.valor).toFixed(2)}
                </p>
                <p style={{ color: '#999', margin: '10px 0' }}>
                  <strong>Vencimento:</strong> {selectedPix.vencimento}
                </p>
              </div>

              {selectedPix.qrcode && (
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ color: '#999', marginBottom: '10px' }}>Código PIX (Copia e Cola):</p>
                  <div style={{
                    backgroundColor: '#2a2a2a',
                    padding: '15px',
                    borderRadius: '8px',
                    wordBreak: 'break-all',
                    color: '#6EE7B7',
                    fontSize: '12px',
                    maxHeight: '100px',
                    overflowY: 'auto'
                  }}>
                    {selectedPix.qrcode}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedPix.qrcode);
                      alert('Código PIX copiado!');
                    }}
                    style={{
                      marginTop: '10px',
                      padding: '10px 20px',
                      backgroundColor: '#6EE7B7',
                      color: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Copiar Código PIX
                  </button>
                </div>
              )}

              <button
                onClick={() => setSelectedPix(null)}
                style={{
                  padding: '15px 30px',
                  backgroundColor: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </div>

      <img src={fxRightImg} className="fx-right-img" alt="decoration" />
      <img src={fxLeftImg} className="fx-left-img" alt="decoration" />
    </React.Fragment>
  );
};

export default Billings;
